/**
 * Layout shell - sidebar + main content. Mobile-responsive (slide-in
 * sidebar with overlay below lg). Per CHAP-78 (Phase 1C #44).
 */
import { useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './Sidebar.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { ErrorBoundary } from '../ErrorBoundary.js';
import { useTenantData } from '../../hooks/useTenantData.js';
import { resolveActiveRole } from '../../lib/active-role.js';
import { useUserRoles } from '../../hooks/useUserRoles.js';
import type { Role } from '../../../shared/types.js';

interface LayoutProps {
  variant: 'customer' | 'admin';
}

export function Layout({ variant }: LayoutProps) {
  const location = useLocation();
  const params = useParams<{ slug?: string }>();
  const tenantSlug = params.slug;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const roles = useUserRoles();
  const activeRole: Role = resolveActiveRole({
    url: location.pathname,
    primaryRole: roles.primaryRole,
    allowedRoles: roles.allowedRoles,
  });

  // CHAP-78 AC#5: the chrome must not go silently blank while tenant config is
  // in flight or after it errors. A bare '' default rendered an empty header
  // that looked like a broken page; fall back to a neutral placeholder while
  // loading and to the product name if the fetch fails.
  const { data: tenant, isLoading: tenantLoading } = useTenantData(tenantSlug ?? 'omega-zeta');
  const tenantDisplayName = variant === 'customer'
    ? (tenant?.tenant.displayName ?? (tenantLoading ? 'Loading…' : 'ChapterBuild'))
    : 'ChapterBuild Admin';

  return (
    <div className="min-h-screen flex min-w-0">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 z-50
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <Sidebar
          pathname={location.pathname}
          tenantSlug={tenantSlug}
          activeRole={activeRole}
          onNavigate={() => setSidebarOpen(false)}
        />
      </aside>

      {/* Main content */}
      <div className="content-surface flex-1 lg:ml-64 min-w-0 flex flex-col h-screen">
        {/* Impersonation banner (CHAP-172) - only renders when impersonating */}
        <ImpersonationBanner />
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-3 py-2.5 flex items-center gap-2.5 min-w-0">
          <button
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
            className="text-navy hover:text-navy-dark flex-shrink-0"
          >
            <MenuIcon />
          </button>
          <div className="min-w-0">
            <h1 className="text-navy font-bold text-base leading-tight truncate">{tenantDisplayName}</h1>
          </div>
        </header>

        {/* Page content - wrapped in ErrorBoundary so a thrown render doesn't blank the layout */}
        <main className="p-3 sm:p-4 lg:p-6 min-w-0 flex-1 flex flex-col min-h-0">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>

        {/* Persistent legal footer (CHAP-159). Visible from every authed
            page; mirrors the same 3 links on the public landing footer
            so legal pages are always one click away. Minimal vertical
            footprint so it doesn't compete with the page content. */}
        <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-3 sm:px-4 lg:px-6 py-2 flex items-center justify-between gap-3 flex-wrap text-xs text-neutral min-w-0">
          <nav className="flex items-center gap-3 flex-wrap min-w-0" aria-label="Legal">
            <a href="/legal/terms" className="hover:text-navy hover:underline">Terms</a>
            <a href="/legal/privacy" className="hover:text-navy hover:underline">Privacy</a>
            <a href="/legal/cookies" className="hover:text-navy hover:underline">Cookies</a>
          </nav>
          <span className="text-[10px] uppercase tracking-wider text-neutral/70">A 1116 LLC project</span>
        </footer>
      </div>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}
